import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { ReportType, apiPostMedicalReportsMultifile } from "../../../api";
import { RequestError } from "../../../api/api-connector";
import { markReadsStale } from "../../../api/query-client";
import { AppModel } from "../../../app/app-model";
import { extractDetailMessage } from "../../../app/error-text";
import { Button } from "../../../components/ui-kit/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "../../../components/ui-kit/dialog";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { DateInput } from "../../../components/ui-kit/fields/date-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { FileUploadDropZone } from "../../../components/ui-kit/file-upload-drop-zone";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { reportTypes } from "./enums";

type ReportFields = {
  report_date: string | null;
  report_type: ReportType | null;
  comment: string;
};

export const AddReportDialog = observer(function AddReportDialog({
  app,
  onClose,
  onSaved,
}: {
  app: AppModel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const patient = app.requirePatient();

  const [fields] = useState<ReportFields>(() =>
    observable({
      report_date: null,
      report_type: null,
      comment: "",
    }),
  );
  const [files, setFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [duplicateNames, setDuplicateNames] = useState<string[]>([]);

  const hasValidFields =
    !!fields.report_date && fields.report_type != null && files.length > 0;

  function clearDuplicateError() {
    setErrorMessage("");
    setDuplicateNames([]);
  }

  async function save() {
    if (!fields.report_date || fields.report_type == null || files.length === 0)
      return;
    clearDuplicateError();
    try {
      const body = new FormData();
      files.forEach((file) => body.append("files", file));
      const response = await apiPostMedicalReportsMultifile({
        // The generated Body type declares `files: string[]`, but the SDK's
        // formDataBodySerializer expects the raw FormData (Blob/File) at runtime.
        body: body as unknown as { files: Array<string> },
        bodySerializer: (body) => body,
        query: {
          patient_id: patient.patientId,
          report_date: fields.report_date,
          report_type: fields.report_type,
          comment: fields.comment || null,
        },
      });

      if (response.data != null) {
        markReadsStale();
        onSaved();
        onClose();
      }
    } catch (error) {
      if (error instanceof RequestError && error.response.status === 409) {
        // Formatted by the shared helper, which also covers the validation-list
        // shape a bare `String(detail)` would print as "[object Object]".
        const detail =
          typeof error.data === "object" &&
          error.data !== null &&
          "detail" in error.data
            ? extractDetailMessage(error.data)
            : t`This document has already been uploaded for this patient.`;
        const named = files.filter((file) => detail.includes(file.name));
        setErrorMessage(detail);
        // The whole-upload wording names no file, so a lone file is the culprit.
        setDuplicateNames(
          named.length > 0
            ? named.map((file) => file.name)
            : files.length === 1
              ? [files[0].name]
              : [],
        );
        return;
      }
      window.handleError(error);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      className="max-h-[90dvh]"
    >
      <DialogHeader>
        <Trans>Add report</Trans>
      </DialogHeader>

      <Form className="flex min-h-0 flex-1 flex-col" onSubmit={() => save()}>
        <DialogBody className="overflow-x-hidden">
          <FormTable dataAdd>
            <FormRow
              label={t`Date`}
              data={
                <DateInput
                  model={fields}
                  attr="report_date"
                  type="date"
                  required
                />
              }
              mandatory
            />
            <FormRow
              label={t`Type`}
              data={
                <Select
                  model={fields}
                  attr="report_type"
                  values={reportTypes}
                  required
                />
              }
              mandatory
            />
            <FormRow
              label={t`Comment`}
              data={
                <TextInput model={fields} attr="comment" multiline="autosize" />
              }
            />
            <FormRow
              label={t`Files`}
              data={
                <FileUploadDropZone
                  multiple
                  onSelect={(selected) => {
                    setFiles(selected);
                    clearDuplicateError();
                  }}
                  accept={[".pdf", ".png", ".jpg", ".jpeg", ".rtf", ".txt"]}
                  flaggedNames={duplicateNames}
                />
              }
              mandatory
            />
          </FormTable>

          <ErrorMessage message={errorMessage} />
        </DialogBody>

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="primary" type="submit" disabled={!hasValidFields}>
            <Trans>Add</Trans>
          </Button>
        </DialogFooter>
      </Form>
    </Dialog>
  );
});
